import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { t } from "ttag";
import z from "zod";

import { ScreenLayout } from "@/app/components/layouts";
import { ROUTES } from "@/app/router";
import { useActiveSubscriptions } from "@/features/billing/hooks";
import { productsQueriesStore } from "@/features/products/api";
import { PriceSelector, TotalSummary } from "@/features/products/components";
import { UpgradeSubscriptionHeader } from "@/features/subscriptions/components";
import { useUpgradeSubscription } from "@/features/subscriptions/hooks";
import { subscriptionsQueries } from "@/features/subscriptions/services";
import { LogoLoader } from "@/shared/components";
import { Price } from "@/shared/domains";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DetailCard,
  Form,
  MarkdownContent,
} from "@/shared/ui";

const formSchema = z.object({
  priceId: Price.shape.id,
  hasConsent: z.literal(true, {
    error: () => t`You must agree before continuing`,
  }),
});

export const UpgradeSubscription = () => {
  const { subscriptionId } = useParams();

  const { data: activeSubscriptions } = useActiveSubscriptions();

  const subscription = activeSubscriptions?.find(
    (subscription) => subscription.id === Number(subscriptionId),
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
  });

  const navigate = useNavigate();

  const {
    data: product,
    isLoading: isLoadingProduct,
    isSuccess: isSuccessProduct,
  } = useQuery(
    subscriptionsQueries.detail(subscription?.id)._ctx.product._ctx
      .withAvailableHigherPrices,
  );

  const {
    data: summary,
    isLoading: isLoadingSummary,
    isPlaceholderData: isPlaceholderSummary,
  } = useQuery({
    ...productsQueriesStore.products.shop._ctx.summary({
      priceId: form.watch("priceId"),
    }),
    placeholderData: (previousData) => previousData,
  });

  const upgradeSubscription = useUpgradeSubscription();

  const isFetchingSummary = isLoadingSummary || isPlaceholderSummary;

  if (!subscription?.isUpgradable) {
    return <Navigate to={ROUTES.BILLING.OVERVIEW} />;
  }

  const handleSubmit = form.handleSubmit(({ priceId }) => {
    const couponCode = product?.prices.find((price) => price.id === priceId)
      ?.discount?.code;

    upgradeSubscription.mutate(
      {
        subscriptionId: subscription?.id,
        productPriceId: priceId,
        couponCode,
      },
      { onSuccess: () => navigate(ROUTES.BILLING.OVERVIEW) },
    );
  });

  return (
    <ScreenLayout>
      <LogoLoader
        show={
          isLoadingProduct || isFetchingSummary || upgradeSubscription.isPending
        }
        withOverlay
        fullscreen={upgradeSubscription.isPending}
      />

      <UpgradeSubscriptionHeader />

      {isSuccessProduct && (
        <Form.Provider {...form}>
          <form
            onSubmit={handleSubmit}
            className="flex w-full flex-col gap-6 self-center lg:max-w-xl"
          >
            <Card.Root className="w-full border">
              <Card.Content className="flex flex-col gap-4">
                <Card.Title weight="weak">{product.category.name}</Card.Title>

                <div className="flex flex-col gap-3">
                  <DetailCard.Root
                    variant="borderLess"
                    className="flex-row gap-2.5 sm:gap-4"
                  >
                    <DetailCard.Inset>
                      <DetailCard.Thumbnail
                        src={product.thumbnail ?? "#"}
                        className="max-w-20 rounded-lg sm:max-w-24 sm:rounded-none"
                      />
                    </DetailCard.Inset>

                    <div className="flex grow flex-col">
                      <DetailCard.Title>{product.name}</DetailCard.Title>
                      <DetailCard.Description className="text-xs sm:text-sm">
                        <MarkdownContent>{product.description}</MarkdownContent>
                      </DetailCard.Description>
                    </div>
                  </DetailCard.Root>

                  <Form.Field
                    control={form.control}
                    name="priceId"
                    render={({ field: { onChange } }) => (
                      <Form.Item>
                        <Form.Control>
                          <PriceSelector
                            options={product.prices}
                            onValueChange={(value) => onChange(Number(value))}
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  />
                </div>
              </Card.Content>

              {summary && <TotalSummary summary={summary} />}
            </Card.Root>

            <div className="flex flex-col items-center gap-2">
              <div className="flex flex-col items-center gap-4">
                <Alert type="warning">
                  {t`Any changes to your subscription will start at the beginning of your next billing cycle. Your current plan stays active until then.`}
                </Alert>

                <Form.Field
                  control={form.control}
                  name="hasConsent"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Form.Item>
                      <Form.Control>
                        <Checkbox.Item
                          checked={value}
                          onCheckedChange={onChange}
                          {...rest}
                        >
                          <Checkbox.Description>
                            {t`I agree to the updated pricing and understand the change will begin with my next billing cycle.`}
                          </Checkbox.Description>
                        </Checkbox.Item>
                      </Form.Control>

                      <Form.Message className="text-center" compact={false} />
                    </Form.Item>
                  )}
                />
              </div>

              <Button
                size="lg"
                type="submit"
                disabled={!form.watch("priceId")}
                className="w-full max-w-70"
              >
                {t`Make payment`}
              </Button>
            </div>
          </form>
        </Form.Provider>
      )}
    </ScreenLayout>
  );
};
